// FuelManagement component - Main container for fuel management section

import { useState, useEffect } from "react";
import FuelForm from "./FuelForm";
import FuelTable from "./FuelTable";
import EditFuelModal from "./EditFuelModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import {
  getFuelRecords,
  createFuelRecord,
  updateFuelRecord,
  deleteFuelRecord,
  getVehicles,
} from "../../services/api";

const FuelManagement = ({ showNotification }) => {
  const [fuelRecords, setFuelRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);

  // Fetch vehicles for license plate suggestions
  useEffect(() => {
    getVehicles().then((result) => {
      if (result.success && result.data) setVehicles(result.data);
    });
  }, []);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    licensePlate: "",
    gasStation: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Fetch fuel records
  useEffect(() => {
    fetchFuelRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.currentPage]);

  const fetchFuelRecords = async () => {
    setLoading(true);
    try {
      const result = await getFuelRecords(
        filters.startDate,
        filters.endDate,
        filters.licensePlate,
        pagination.currentPage,
        pagination.itemsPerPage,
        filters.gasStation,
      );
      if (result.success) {
        setFuelRecords(result.data);

        // Update pagination if server returns pagination info
        if (result.pagination) {
          // console.log("API Pagination:", result.pagination);
          const paginationData = {
            currentPage: result.pagination.page,
            totalPages: result.pagination.totalPages,
            totalItems: result.pagination.totalRecords,
            itemsPerPage: result.pagination.limit,
          };
          // console.log("Mapped Pagination:", paginationData);
          setPagination(paginationData);
        } else {
          // console.log("No pagination in response");
        }
      } else {
        showNotification(result.message, "error");
      }
    } catch (error) {
      console.error("Error fetching fuel records:", error);
      showNotification("เกิดข้อผิดพลาดในการดึงข้อมูล", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const handleAddRecord = async (formData) => {
    const result = await createFuelRecord(formData);
    if (result.success) {
      showNotification(result.message, "success");
      fetchFuelRecords();
    } else {
      showNotification(result.message, "error");
    }
  };

  const handleUpdateRecord = async (id, formData) => {
    const result = await updateFuelRecord(id, formData);
    if (result.success) {
      showNotification(result.message, "success");
      setEditingRecord(null);
      setIsEditModalOpen(false);
      fetchFuelRecords();
    } else {
      showNotification(result.message, "error");
    }
  };

  const handleDeleteRecord = async () => {
    if (!deletingRecord) return;

    const result = await deleteFuelRecord(deletingRecord.id);
    if (result.success) {
      showNotification(result.message, "success");
      setIsDeleteModalOpen(false);
      setDeletingRecord(null);
      fetchFuelRecords();
    } else {
      showNotification(result.message, "error");
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setIsEditModalOpen(true);
  };

  const handleDelete = (record) => {
    setDeletingRecord(record);
    setIsDeleteModalOpen(true);
  };

  const handleCancelEdit = () => {
    setEditingRecord(null);
    setIsEditModalOpen(false);
  };

  const handleCancelDelete = () => {
    setDeletingRecord(null);
    setIsDeleteModalOpen(false);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Reset to page 1 when filters change
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  return (
    <div>
      {/* Fuel Form */}
      <FuelForm
        onSubmit={handleAddRecord}
        editingRecord={null}
        onCancelEdit={null}
      />

      {/* Fuel Table */}
      <FuelTable
        data={fuelRecords}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={loading}
        filters={filters}
        onFilterChange={handleFilterChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        vehicles={vehicles}
      />

      {/* Edit Modal */}
      <EditFuelModal
        isOpen={isEditModalOpen}
        onClose={handleCancelEdit}
        onSubmit={handleUpdateRecord}
        record={editingRecord}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleDeleteRecord}
        record={deletingRecord}
      />
    </div>
  );
};

export default FuelManagement;
